import 'video.js/dist/video-js.min.css';

import { extInfo } from 'lib/extInfo';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_MOBILE_BROWSER, IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentPublicId } from 'reactApp/modules/faces/faces.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { Stub } from 'reactApp/ui/ReactViewer/Stub/Stub';
import { adInit } from 'reactApp/ui/ReactViewer/VideoPlayer/AD';
import { addCustomNavigation } from 'reactApp/ui/ReactViewer/VideoPlayer/CustomNavigation';
import { GaData, VideoPlayerGa } from 'reactApp/ui/ReactViewer/VideoPlayer/VideoPlayerGa';
import { sendXray } from 'reactApp/utils/ga';
import videojs, { VideoJsPlayer } from 'video.js';
import contribQualityLevels from 'videojs-contrib-quality-levels';

import hlsQualitySelector from './hlsQualitySelector/plugin';
import loopPlugin from './LoopPlugin/plugin';
import styles from './VideoPlayer.css';
import { IProps } from './VideoPlayer.types';

const errorsMap = {
    'You aborted the media playback': 'Вы прервали воспроизведение мультимедиа',
    'A network error caused the media download to fail part-way.': 'Ошибка сети привела к частичному сбою загрузки мультимедиа.',
    'The media playback was aborted due to a corruption problem or because the media used features your browser did not support.':
        'Воспроизведение мультимедиа было прервано из-за проблемы с повреждением или из-за того, что мультимедиа использовало функции, которые не поддерживал ваш браузер.',
    'The media could not be loaded, either because the server or network failed or because the format is not supported.':
        'Не удалось загрузить файл из-за отказа сервера или сети или из-за того, что формат не поддерживается.',
    'The media is encrypted and we do not have the keys to decrypt it.': 'Носитель зашифрован, и у нас нет ключей для его расшифровки.',
    'No compatible source was found for this media.': 'Ошибка воспроизведения. Неизвестный кодек видео.',
};

const errorsMapByCode = {
    1: 'Вы прервали воспроизведение мультимедиа',
    2: 'Ошибка сети привела к частичному сбою загрузки мультимедиа.',
    3: 'Воспроизведение мультимедиа было прервано из-за проблемы с повреждением или из-за того, что мультимедиа использовало функции, которые не поддерживал ваш браузер.',
    4: 'Не удалось загрузить файл из-за отказа сервера или сети или из-за того, что формат не поддерживается.',
    5: 'Носитель зашифрован, и у нас нет ключей для его расшифровки.',
    6: 'Ошибка воспроизведения. Неизвестный кодек видео.',
};

videojs.addLanguage('ru', {
    Play: 'Смотреть',
    Pause: 'Пауза',
    Quality: 'Качество',
    Mute: 'Отключение звука',
    Unmute: 'Включение звука',
    Auto: 'Авто',
    auto: 'авто',
    'Playback Rate': 'Скорость воспроизведения',
    'Picture-in-Picture': 'Мини-плеер',
    'Exit Picture-in-Picture': 'Мини-плеер',
    Fullscreen: 'Во весь экран',
    'Non-Fullscreen': 'Свернуть',
    Loop: 'Повтор',
    'Non-Loop': 'Повтор',
    ...errorsMap,
});

videojs.registerPlugin('contribQualityLevels', contribQualityLevels);
videojs.registerPlugin('hlsQualitySelector', hlsQualitySelector);
videojs.registerPlugin('loopPlugin', loopPlugin);

const VideoPlayerComponent = (props: IProps): ReactElement => {
    const {
        streamUrl,
        posterUrl,
        kind,
        ext,
        name,
        isVisible = true,
        autoPlay = false,
        nativeUrl,
        size,
        isArchive = false,
        isAttaches = false,
        onError,
        sendRadar,
    } = props;
    const [adm, setAdm] = useState<any | undefined>();
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    const storage = useSelector(getCurrentStorage);
    const videoAdEnabled = useSelector(ViewerSelectors.isVideoAdEnabled);
    const publicId = useSelector(getCurrentPublicId);
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<VideoJsPlayer>();
    const isVideo = kind === 'video';

    const handleError = useCallback((error: { code?: number; message?: string } | null) => {
        const { code = 0, message = 'Что-то пошло не так' } = error || {};

        if (!playerRef.current?.error()) {
            onError?.(errorsMap[message] || errorsMapByCode[code] || message);
            setTimeout(() => {
                playerRef.current?.error(errorsMap[message] || errorsMapByCode[code] || message);
                playerRef.current?.removeClass('vjs-waiting');
            }, 0);
        }
    }, []);

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }

        const controlBarSettings: videojs.ControlBarOptions = {
            currentTimeDisplay: true,
            timeDivider: true,
            // Ломает перемещение трека ProgressControl вслед за курсором при перетасковании
            // прячем через CSS
            // remainingTimeDisplay: false,
            fullscreenToggle: isVideo,
            pictureInPictureToggle: false,
            volumePanel: !isPhone,
            playToggle: !isPhone,
        };

        playerRef.current = videojs(videoRef.current, {
            language: 'ru',
            bigPlayButton: false,
            controls: true,
            playbackRates: isVideo ? [0.5, 1, 1.25, 1.5, 2] : undefined,
            autoplay: autoPlay,
            controlBar: controlBarSettings,
            fill: true,
            html5: {
                vhs: {
                    overrideNative: !videojs.browser.IS_SAFARI,
                    withCredentials: true,
                },
                nativeAudioTracks: videojs.browser.IS_SAFARI,
                nativeVideoTracks: videojs.browser.IS_SAFARI,
            },
        });

        const player = playerRef.current;

        const gaData: GaData = {
            source: IS_PUBLIC_ALBUM ? 'album' : storage,
            type_content: kind,
            is_stories: false,
            have_face: false,
            id_public: publicId,
            extension: ext?.toLowerCase(),
            is_archive: isArchive,
            is_attache: isAttaches,
            size_files: size,
            name,
            is_touch: IS_MOBILE_BROWSER,
        };

        const ga = new VideoPlayerGa(player, gaData);
        ga.subscribe();

        // disable SwipingComponent logic for controlBar
        player.controlBar.el().addEventListener('pointerdown', (event) => {
            event.stopPropagation();
        });

        const info = extInfo.get(ext);

        if (streamUrl && streamUrl.includes('.m3u8') && info.isStreamingSupported) {
            player.src({
                src: streamUrl,
                type: extInfo.get('m3u8').mimeType,
            });
        } else if (info.isNativeSupported) {
            player.src({
                src: nativeUrl,
                type: info.mimeType,
            });
        } else {
            sendXray(['video-player', 'error', 'not-supported']);
            handleError({ message: 'Данный формат не потдерживается' });
        }

        player.load();

        player.poster(posterUrl);

        if (streamUrl && info.isStreamingSupported && info.isNativeSupported) {
            // fallback logic
            let streamUrlError = false;
            player.on('error', () => {
                const error = player.error();

                if (error && [4].includes(error.code) && !streamUrlError) {
                    streamUrlError = true;
                    player.error(null);
                    player.src({
                        src: nativeUrl,
                        type: info.mimeType,
                    });

                    return;
                }

                // Чистим ошибку чтобы выбросить кастомную
                if (error?.code) {
                    player.error(null);
                }
                handleError(error);
            });
        } else {
            player.on('error', () => {
                const error = player.error();

                // Чистим ошибку чтобы выбросить кастомную
                if (error?.code) {
                    player.error(null);
                }
                handleError(error);
            });
        }

        player.ready(() => {
            addCustomNavigation({ player, isPhone, isVideo });

            player.loopPlugin();

            if (isVideo) {
                player.hlsQualitySelector({
                    displayCurrentQuality: true,
                });

                // отключаем фиксацию меню по клику
                setTimeout(() => {
                    const qualitySelector = playerRef.current?.$('.vjs-quality-selector');
                    qualitySelector?.addEventListener('mouseleave', () => {
                        (player.$('.vjs-quality-selector .vjs-menu-item') as HTMLElement)?.blur();
                    });
                }, 0);
            }
        });

        if (isVideo && videoAdEnabled) {
            const initAd = () => {
                const { adm } = adInit({ player, controlBarSettings, isPhone, isVideo, gaData });
                setAdm(adm);
                player?.off('playing', initAd);

                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState !== 'visible') {
                        adm.pause();
                    } else {
                        adm.play();
                    }
                });
            };

            player?.on('playing', initAd);
        }

        return () => {
            // dispose также удаляет из ДОМ <video, что может быть проблемой при повторном использовании
            // https://github.com/videojs/video.js/issues/4970
            player.dispose();
            ga.unsubscribe();
        };
    }, []);

    // При переходе на другой элемент viewer компонент не совершает unmount, ставим на паузу
    useEffect(() => {
        if (!isVisible) {
            playerRef.current?.pause();
            adm?.pause();
        }
    }, [isVisible, adm]);

    return (
        <div className={`${styles.root} ${isPhone ? 'mobile' : ''}`}>
            <video ref={videoRef} className="video-js" />
            {!isVideo && (
                <div className={styles.audioIcon}>
                    <Stub ext={ext} kind={kind} isVirus={false} name={name} sendRadar={sendRadar} />
                </div>
            )}
        </div>
    );
};

// При переходе на другой элемент в архиве videojs делает dispose и удаляет <video из ДОМ,
// это создает проблему с videoRef при повторном переключении на этот же файл
// Поэтому сделана эта обертка с key, чтобы целиком удалять из ДОМ всё и заново рендерить вместе с новым <video
export const VideoPlayer = (props: IProps): ReactElement => {
    const { name, ...rest } = props;

    return <VideoPlayerComponent key={encodeURIComponent(name)} {...rest} name={name} />;
};
